import { Validators } from '@biteinc/common';
import { allEnumValues, BalanceTransactionType, DiscountType, FulfillmentMethod, Gateway, GroupOrderingMode, IntegrationSystem, KioskOs, LanguageCode, OrderPaymentDestination, OrderPrintState, OrderStatus, OrderState, RewardType, RewardApiVersion, ServiceChargeSource, } from '@biteinc/enums';
import { AbstractModelSchema } from './abstract_model_schema.js';
const loyaltyRewardV2Fields = {
    rewardApiVersion: {
        type: 'stringEnum',
        enum: RewardApiVersion,
    },
    name: {
        type: 'shortString',
    },
    description: {
        type: 'shortString',
    },
    localizationNameKey: {
        type: 'keyString',
    },
    localizationNameWithAmountKey: {
        type: 'keyString',
    },
    localizationDescriptionKey: {
        type: 'keyString',
    },
    type: {
        type: 'int',
        values: allEnumValues(RewardType),
    },
    amount: {
        type: 'price',
    },
    canAutoApply: {
        type: 'bool',
    },
    rewardI9nId: {
        type: 'keyString',
    },
    pointsUser: {
        type: 'int',
    },
    pointsUsed: {
        type: 'int',
    },
    pointsCode: {
        type: 'keyString',
    },
    itemI9nId: {
        type: 'keyString',
    },
    quantityOfItemToDiscount: {
        type: 'int',
    },
    i9nType: {
        type: 'keyString',
    },
    i9nId: {
        type: 'keyString',
    },
    shouldBeHidden: {
        type: 'bool',
    },
    originalRewardI9nId: {
        type: 'keyString',
    },
    rewardI9nItems: {
        type: 'array',
        elementType: 'object',
        isFreeform: true,
    },
    notCurrentlyRedeemable: {
        type: 'bool',
    },
    isApplicable: {
        type: 'bool',
    },
    imageUrl: {
        type: 'url',
    },
    transactionId: {
        type: 'mongoId',
    },
    serializedI9nReward: {
        type: 'keyString',
    },
    isRewardCode: {
        type: 'bool',
    },
    metadata: {
        type: 'object',
        isFreeform: true,
    },
};
const appliedCouponFields = {
    integrationId: {
        type: 'mongoId',
    },
    i9nId: {
        type: 'keyString',
    },
    name: {
        type: 'keyString',
    },
    type: {
        type: 'int',
        values: allEnumValues(RewardType),
    },
    amount: {
        type: 'price',
    },
    couponAppliedAt: {
        type: 'timestamp',
    },
    calculatedAmount: {
        type: 'price',
    },
};
const integrationFields = {
    _id: {
        type: 'mongoId',
        required: true,
    },
    integrationId: {
        type: 'mongoId',
    },
    vendorId: {
        type: 'mongoId',
        required: true,
    },
    system: {
        type: 'int',
        values: allEnumValues(IntegrationSystem),
        required: true,
    },
    subTotal: {
        type: 'price',
        // This field will only be set for POS integrations
    },
    taxTotal: {
        type: 'price',
        // This field will only be set for POS integrations
    },
    discountTotal: {
        type: 'price',
        // This field will only be set for POS integrations
    },
    tipTotal: {
        type: 'price',
        // This field will only be set for POS integrations
    },
    serviceChargeTotal: {
        type: 'price',
        // This field will only be set for POS integrations
    },
    donationTotal: {
        type: 'price',
        // This field will only be set for POS integrations
    },
    total: {
        type: 'price',
        // This field will only be set for POS integrations
    },
    validatedAt: {
        type: 'timestamp',
    },
    validationWasSkipped: {
        type: 'keyString',
    },
    validationData: {
        type: 'object',
        isFreeform: true,
    },
    createdOnPosAt: {
        type: 'timestamp',
    },
    creationData: {
        type: 'object',
        isFreeform: true,
    },
    creationWasSkipped: {
        type: 'keyString',
    },
    sentAt: {
        type: 'timestamp',
    },
    sendingWasSkipped: {
        type: 'keyString',
    },
    sendData: {
        type: 'object',
        isFreeform: true,
    },
    updateData: {
        type: 'object',
        isFreeform: true,
    },
    updateWasSkipped: {
        type: 'keyString',
    },
    voidedAt: {
        type: 'timestamp',
    },
    voidingWasSkipped: {
        type: 'keyString',
    },
    rewardsAppliedAt: {
        type: 'timestamp',
    },
    rewards: {
        type: 'array',
        elementType: 'object',
        fields: {
            ...loyaltyRewardV2Fields,
            posDiscountId: {
                type: 'keyString',
            },
        },
    },
    fetchedRewards: {
        type: 'array',
        elementType: 'object',
        fields: {
            ...loyaltyRewardV2Fields,
            posDiscountId: {
                type: 'keyString',
            },
        },
    },
    fetchedRewardsHash: { type: 'keyString' },
    loyaltyToken: { type: 'keyString' },
    loyaltyGuestData: {
        type: 'object',
        fields: {
            guestEmail: { type: 'email' },
            guestPhoneNumber: { type: 'phone' },
            guestName: { type: 'keyString' },
            statusDescription: { type: 'shortString' },
        },
    },
    pointsBalance: { type: 'int' },
    transactionI9nId: { type: 'mongoId' },
    compCardToken: { type: 'keyString' },
    updatedOnPosAt: { type: 'timestamp' },
    storedValueAuthDataToken: { type: 'keyString' },
    storedValueRemainingBalance: { type: 'price' },
    storedValueAuthData: { type: 'object', isFreeform: true },
    sendAttempts: {
        type: 'array',
        elementType: 'object',
        fields: {
            startedAt: {
                type: 'timestamp',
            },
            endedAt: {
                type: 'timestamp',
            },
            error: {
                type: 'object',
                isFreeform: true,
            },
            isFinalAttempt: {
                type: 'bool',
            },
        },
    },
    autoAppliedDiscountIds: {
        type: 'array',
        elementType: 'keyString',
    },
    discounts: {
        type: 'array',
        elementType: 'object',
        fields: {
            i9nId: {
                type: 'keyString',
                isPermanent: true,
            },
            name: {
                type: 'keyString',
                isPermanent: true,
            },
            type: {
                type: 'int',
                values: allEnumValues(DiscountType),
            },
            amount: {
                type: 'price',
                isPermanent: true,
            },
            posDiscountId: {
                type: 'keyString',
                isPermanent: true,
            },
            itemIds: {
                type: 'array',
                elementType: 'mongoId',
                isPermanent: true,
            },
            i9nData: {
                type: 'object',
                isPermanent: true,
                isFreeform: true,
            },
        },
    },
    serviceCharges: {
        type: 'array',
        elementType: 'object',
        fields: {
            name: {
                type: 'keyString',
                isPermanent: true,
            },
            amount: {
                type: 'price',
                isPermanent: true,
            },
            source: {
                type: 'stringEnum',
                enum: ServiceChargeSource,
                isPermanent: true,
            },
            transactionId: {
                type: 'mongoId',
                isPermanent: true,
                conditions: [{ field: 'source', values: [ServiceChargeSource.Payment] }],
            },
        },
    },
    taxes: {
        type: 'array',
        elementType: 'object',
        fields: {
            i9nId: {
                type: 'keyString',
                isPermanent: true,
            },
            name: {
                type: 'keyString',
                isPermanent: true,
            },
            amount: {
                type: 'price',
                isPermanent: true,
            },
        },
    },
};
export const orderSchema = {
    displayName: 'order',
    type: 'object',
    fields: {
        ...AbstractModelSchema.fields,
        orgId: {
            type: 'mongoId',
            required: true,
            isPermanent: true,
        },
        siteId: {
            type: 'mongoId',
            required: true,
            isPermanent: true,
        },
        locationId: {
            type: 'mongoId',
            required: true,
            isPermanent: true,
        },
        clientId: {
            type: 'mongoId',
            required: true,
            isPermanent: true,
        },
        userId: {
            type: 'mongoId',
            isPermanent: true,
        },
        customerId: {
            type: 'mongoId',
            isPermanent: true,
        },
        clonedFrom: {
            type: 'mongoId',
            isReadOnly: true,
        },
        recommendationVariationId: {
            type: 'mongoId',
            isReadOnly: true,
        },
        clientVersion: {
            type: 'int',
            isPermanent: true,
        },
        loyaltyAuthData: {
            type: 'object',
            isFreeform: true,
            isReadOnly: true,
        },
        kioskId: {
            type: 'mongoId',
            isPermanent: true,
        },
        clientNumber: {
            type: 'keyString',
            isPermanent: true,
        },
        gcnBuildName: { type: 'keyString', isPermanent: true },
        garconBuildName: {
            type: 'keyString',
            isPermanent: true,
        },
        vitrineBuildName: {
            type: 'keyString',
            isPermanent: true,
        },
        kioskBuild: { type: 'int' },
        kioskOs: { type: 'stringEnum', enum: KioskOs, isPermanent: true },
        kioskOsVersion: { type: 'keyString', isPermanent: true },
        kioskName: { type: 'keyString', isPermanent: true },
        userAgent: { type: 'keyString', isPermanent: true },
        referer: { type: 'keyString', isPermanent: true },
        sequenceNumber: {
            type: 'int',
            isPermanent: true,
        },
        includeUtensils: {
            type: 'bool',
        },
        clientState: {
            type: 'int',
            isReadOnly: true,
        },
        isKioskPreview: {
            type: 'bool',
            isPermanent: true,
        },
        isDemo: {
            type: 'bool',
            isPermanent: true,
        },
        orderedItemsLeadTime: {
            type: 'int',
            isReadOnly: true,
        },
        originalSubTotal: {
            type: 'price',
            isReadOnly: true,
        },
        startedAt: {
            type: 'timestamp',
            isPermanent: true,
        },
        sessionId: {
            type: 'keyString',
            isPermanent: true,
        },
        groupOrderingMode: {
            type: 'stringEnum',
            enum: GroupOrderingMode,
        },
        preTaxExemptTaxTotal: {
            type: 'price',
        },
        masterNumber: {
            type: 'keyString',
        },
        userDidRejectConsent: {
            type: 'bool',
        },
        guestSurveyMaximum: {
            type: 'int',
            isReadOnly: true,
        },
        wasValidated: {
            type: 'timestamp',
            isPermanent: true,
        },
        deliveryInstructions: { type: 'keyString' },
        validationWasRequestedAt: { type: 'timestamp', isReadOnly: true },
        validationHash: { type: 'keyString', isReadOnly: true },
        tipWasSetByGuest: { type: 'bool', isReadOnly: true },
        validationWasSkipped: {
            type: 'keyString',
            isPermanent: true,
        },
        pickupAt: {
            type: 'timestamp',
        },
        wasSentToPos: {
            type: 'timestamp',
            isPermanent: true,
        },
        wasSentForFulfillment: {
            type: 'bool',
            isPermanent: true,
        },
        sendingToPosWasSkipped: {
            type: 'keyString',
            isPermanent: true,
        },
        guestId: {
            type: 'mongoId',
            isPermanent: true,
        },
        guestConsentedToMarketing: {
            type: 'bool',
            isPermanent: true,
        },
        channel: {
            type: 'keyString',
            isReadOnly: true,
        },
        paymentDestination: {
            type: 'int',
            values: allEnumValues(OrderPaymentDestination),
            isPermanent: true,
        },
        fulfillmentMethod: {
            type: 'int',
            values: allEnumValues(FulfillmentMethod),
            required: true,
            isPermanent: true,
        },
        menuStructureId: {
            type: 'mongoId',
            isPermanent: true,
        },
        outpostId: {
            type: 'mongoId',
        },
        tableNumber: {
            type: 'keyString',
            isPermanent: true,
            validationRegex: Validators.simpleStringRegexString({ rejectEmptySpace: true }),
        },
        guestName: {
            type: 'keyString',
            isPermanent: true,
            validationRegex: Validators.simpleStringRegexString({ rejectEmptySpace: true }),
        },
        guestPhoneNumber: {
            type: 'phone',
            isPermanent: true,
        },
        guestEmail: {
            type: 'email',
            isPermanent: true,
        },
        receiptSentAt: {
            type: 'timestamp',
            isReadOnly: true,
        },
        receiptSendError: {
            type: 'object',
            isFreeform: true,
            isReadOnly: true,
        },
        guestVehicle: {
            type: 'object',
            fields: {
                make: {
                    type: 'keyString',
                    required: true,
                    validationRegex: Validators.simpleStringRegexString(),
                },
                model: {
                    type: 'keyString',
                    required: true,
                    validationRegex: Validators.simpleStringRegexString(),
                },
                color: {
                    type: 'keyString',
                    required: true,
                    validationRegex: Validators.simpleStringRegexString(),
                },
            },
        },
        deliveryAddress: {
            type: 'object',
            subtype: 'DeliveryAddress',
        },
        outpost: {
            type: 'object',
            isReadOnly: true,
            fields: {
                _id: {
                    type: 'mongoId',
                },
                name: {
                    type: 'keyString',
                },
                pickupInstructions: {
                    type: 'keyString',
                },
                instructionalImageUrl: {
                    type: 'url',
                },
                roomNumber: {
                    type: 'keyString',
                },
            },
        },
        hasLoyaltyProgram: {
            type: 'bool',
            isPermanent: true,
        },
        subTotal: {
            type: 'price',
            required: true,
            isPermanent: true,
        },
        taxTotal: {
            type: 'price',
            isPermanent: true,
        },
        discountTotal: {
            type: 'price',
            isPermanent: true,
        },
        tipTotal: {
            type: 'price',
            isPermanent: true,
        },
        serviceChargeTotal: {
            type: 'price',
            isPermanent: true,
            isReadOnly: true,
        },
        donationTotal: {
            type: 'price',
            isPermanent: true,
        },
        total: {
            type: 'price',
            isPermanent: true,
        },
        isTaxExempt: {
            type: 'bool',
        },
        orderedItems: {
            type: 'array',
            elementType: 'object',
            elementSubtype: 'OrderedItemDb',
        },
        clientOrderedItems: {
            type: 'array',
            elementType: 'object',
            elementSubtype: 'OrderedItemDb',
        },
        preferences: {
            type: 'object',
            fields: {
                'gcn:setting:textSize': {
                    type: 'keyString',
                },
                'gcn:setting:language': {
                    type: 'keyString',
                },
                'gcn:setting:badges': {
                    type: 'array',
                    elementType: 'mongoId',
                },
            },
        },
        refunds: {
            type: 'array',
            elementType: 'object',
            isReadOnly: true,
            fields: {
                _id: {
                    type: 'mongoId',
                },
                accessUserId: {
                    type: 'mongoId',
                },
                transactionId: {
                    type: 'mongoId',
                },
                refundTransactionId: {
                    type: 'mongoId',
                },
                state: {
                    type: 'int',
                },
                subTotal: {
                    type: 'price',
                },
                taxTotal: {
                    type: 'price',
                },
                total: {
                    type: 'price',
                },
                discountTotal: {
                    type: 'price',
                },
                tipTotal: {
                    type: 'price',
                },
                donationTotal: {
                    type: 'price',
                },
                serviceChargeTotal: {
                    type: 'price',
                },
                accessUsername: {
                    type: 'keyString',
                },
                createdAt: {
                    type: 'timestamp',
                },
            },
        },
        balanceTransactions: {
            type: 'array',
            elementType: 'object',
            isReadOnly: true,
            fields: {
                vendorId: {
                    type: 'mongoId',
                },
                amount: {
                    type: 'price',
                },
                gateway: {
                    type: 'int',
                    values: allEnumValues(Gateway),
                },
                type: {
                    type: 'stringEnum',
                    enum: BalanceTransactionType,
                },
                createdAt: {
                    type: 'timestamp',
                },
                i9nId: {
                    type: 'keyString',
                },
                feeAmount: {
                    type: 'price',
                },
                reversalId: {
                    type: 'keyString',
                },
            },
        },
        statusUpdates: {
            type: 'array',
            elementType: 'object',
            isReadOnly: true,
            fields: {
                integrationId: {
                    type: 'mongoId',
                },
                status: {
                    type: 'stringEnum',
                    enum: OrderStatus,
                },
                statusUpdatedAt: {
                    type: 'timestamp',
                },
                i9nDescription: {
                    type: 'keyString',
                },
            },
        },
        appliedRewards: {
            type: 'array',
            elementType: 'object',
            isReadOnly: true,
            fields: {
                ...loyaltyRewardV2Fields,
                posIntegrationId: {
                    type: 'mongoId',
                },
                integrationId: {
                    type: 'mongoId',
                },
                rewardAppliedAt: {
                    type: 'timestamp',
                },
            },
        },
        appliedCompCards: {
            type: 'array',
            elementType: 'object',
            isReadOnly: true,
            fields: {
                posIntegrationId: {
                    type: 'mongoId',
                },
                integrationId: {
                    type: 'mongoId',
                },
                rewardApiVersion: {
                    type: 'keyString',
                },
                i9nId: {
                    type: 'keyString',
                },
                name: {
                    type: 'keyString',
                },
                amount: {
                    type: 'price',
                },
                type: {
                    type: 'int',
                    values: allEnumValues(RewardType),
                },
                i9nItems: {
                    type: 'array',
                    elementType: 'object',
                    fields: {
                        i9nId: {
                            type: 'keyString',
                        },
                    },
                },
                rewardAppliedAt: {
                    type: 'timestamp',
                },
                system: {
                    type: 'int',
                    values: allEnumValues(IntegrationSystem),
                },
            },
        },
        appliedPosCoupons: {
            type: 'array',
            elementType: 'object',
            isReadOnly: true,
            fields: {
                integrationId: {
                    type: 'mongoId',
                },
                itemId: {
                    type: 'mongoId',
                },
                itemIds: {
                    type: 'array',
                    elementType: 'mongoId',
                },
                modifierIds: {
                    type: 'array',
                    elementType: 'mongoId',
                },
                i9nId: {
                    type: 'keyString',
                },
                name: {
                    type: 'keyString',
                },
                type: {
                    type: 'int',
                    values: allEnumValues(RewardType),
                },
                amount: {
                    type: 'price',
                },
                couponAppliedAt: {
                    type: 'timestamp',
                },
                calculatedAmount: {
                    type: 'price',
                },
                posDiscountId: {
                    type: 'keyString',
                },
                itemI9nIds: {
                    type: 'array',
                    elementType: 'keyString',
                },
            },
        },
        appliedBiteCoupons: {
            type: 'array',
            elementType: 'object',
            isReadOnly: true,
            fields: {
                ...appliedCouponFields,
                managedCouponId: {
                    type: 'mongoId',
                },
                itemId: {
                    type: 'mongoId',
                },
                posDiscountId: {
                    type: 'keyString',
                },
                calculatedRate: {
                    type: 'float',
                },
                singleUseCouponCode: {
                    type: 'keyString',
                },
                posDiscountIsOpen: {
                    type: 'bool',
                },
                retireAtOrgLevel: {
                    type: 'bool',
                },
            },
        },
        appliedLoyaltyCoupons: {
            type: 'array',
            elementType: 'object',
            isReadOnly: true,
            fields: {
                ...appliedCouponFields,
                i9nType: {
                    type: 'keyString',
                },
                rewardI9nId: {
                    type: 'keyString',
                },
            },
        },
        itemPriceCorrectionById: {
            type: 'map',
            isReadOnly: true,
            key: {
                type: 'mongoId',
            },
            fields: {
                unitPrice: {
                    type: 'price',
                },
                name: {
                    type: 'shortString',
                },
            },
        },
        itemComboCorrectionById: {
            type: 'map',
            isReadOnly: true,
            key: {
                type: 'mongoId',
            },
            fields: {
                comboI9nId: {
                    type: 'keyString',
                },
                comboName: {
                    type: 'shortString',
                },
                comboPrice: {
                    type: 'price',
                },
            },
        },
        loyaltyOrderIds: {
            type: 'array',
            elementType: 'keyString',
            isReadOnly: true,
        },
        fulfillmentOrderIds: {
            type: 'array',
            elementType: 'keyString',
            isReadOnly: true,
        },
        allBalanceTransactionsCreated: {
            type: 'bool',
            isReadOnly: true,
        },
        allBalanceTransactionsReversed: {
            type: 'bool',
            isReadOnly: true,
        },
        unappliedRewards: {
            type: 'array',
            elementType: 'object',
            fields: loyaltyRewardV2Fields,
            isReadOnly: true,
        },
        recommendationExperimentConfiguration: {
            type: 'object',
            isFreeform: true,
            isReadOnly: true,
        },
        processingIntervalById: {
            type: 'map',
            isReadOnly: true,
            key: { type: 'keyString' },
            fields: {
                id: { type: 'keyString' },
                start: { type: 'timestamp' },
                end: { type: 'timestamp' },
            },
        },
        expoPrintState: {
            type: 'int',
            values: allEnumValues(OrderPrintState),
            isReadOnly: true,
        },
        expoPrintRequestedAt: { type: 'timestamp', isReadOnly: true },
        hasBeenCoveredByAuditVersion: { type: 'keyString', isReadOnly: true },
        numberOfFailedLoyaltySignInAttempts: {
            type: 'int',
            isReadOnly: true,
        },
        numberOfFailedGiftCardAccessAttempts: {
            type: 'int',
            isReadOnly: true,
        },
        serviceCharges: {
            type: 'array',
            elementType: 'object',
            isReadOnly: true,
            fields: {
                name: {
                    type: 'keyString',
                    isPermanent: true,
                },
                amount: {
                    type: 'price',
                    isPermanent: true,
                },
                source: {
                    type: 'stringEnum',
                    enum: ServiceChargeSource,
                    isPermanent: true,
                },
                transactionId: {
                    type: 'mongoId',
                    isPermanent: true,
                    conditions: [{ field: 'source', values: [ServiceChargeSource.Payment] }],
                },
            },
        },
        validatedDiscountLabels: {
            type: 'array',
            elementType: 'object',
            isReadOnly: true,
            fields: {
                name: {
                    type: 'keyString',
                    isPermanent: true,
                },
                localizationNameKey: {
                    type: 'keyString',
                    isPermanent: true,
                },
                type: {
                    type: 'int',
                    values: allEnumValues(DiscountType),
                },
                amount: {
                    type: 'price',
                    isPermanent: true,
                },
                i9nId: {
                    type: 'keyString',
                    isPermanent: true,
                },
                itemIds: {
                    type: 'array',
                    elementType: 'mongoId',
                    isPermanent: true,
                },
                amountByOrderedItemId: {
                    type: 'object',
                    isPermanent: true,
                    isFreeform: true,
                },
            },
        },
        integrationById: {
            type: 'map',
            isPermanent: true,
            key: {
                type: 'mongoId',
            },
            fields: integrationFields,
        },
        dataForVendors: {
            type: 'array',
            elementType: 'object',
            isPermanent: true,
            fields: integrationFields,
        },
        guestSurveyValue: {
            type: 'int',
            min: 0,
            max: 3,
        },
        receiptShortCode: {
            type: 'keyString',
            isReadOnly: true,
        },
        lockExpiresAt: {
            type: 'timestamp',
            isReadOnly: true,
        },
        lockLeaseId: {
            type: 'keyString',
            isReadOnly: true,
        },
        lockDomain: {
            type: 'keyString',
            isReadOnly: true,
        },
        isClosed: {
            type: 'bool',
            isReadOnly: true,
        },
        closedAt: {
            type: 'timestamp',
            isReadOnly: true,
        },
        isBiteFutureOrder: {
            type: 'bool',
            isReadOnly: true,
        },
        isI9nFutureOrder: {
            type: 'bool',
            isReadOnly: true,
        },
        ordersApiVersion: {
            type: 'keyString',
            isReadOnly: true,
        },
        posOrderIds: {
            type: 'array',
            elementType: 'keyString',
            isReadOnly: true,
            isHidden: true,
        },
        originEnvironment: {
            type: 'keyString',
            isReadOnly: true,
            isHidden: true,
        },
        clientEvents: {
            type: 'array',
            isHidden: true,
            elementType: 'object',
            elementSubtype: 'OrderEvent',
            minCount: 1,
            maxCount: 10000,
        },
        isCancelled: {
            type: 'bool',
            isReadOnly: true,
        },
        cancelledAt: {
            type: 'timestamp',
            isReadOnly: true,
        },
        abandonedAt: {
            type: 'timestamp',
            isReadOnly: true,
        },
        addedSendOrderJobAt: {
            type: 'timestamp',
            isReadOnly: true,
        },
        selectedLanguage: {
            type: 'stringEnum',
            enum: LanguageCode,
        },
        hasTransactions: {
            type: 'bool',
            isReadOnly: true,
        },
        transactionIds: {
            type: 'array',
            elementType: 'mongoId',
            isReadOnly: true,
        },
        isClosedOrIsFinished: {
            type: 'bool',
            isReadOnly: true,
        },
        hasFailedToSendToPos: {
            type: 'bool',
            isReadOnly: true,
        },
        state: {
            type: 'stringEnum',
            enum: OrderState,
            isReadOnly: true,
        },
    },
};
//# sourceMappingURL=order_schema.js.map